import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import fr_FR from '@react-pdf-viewer/locales/lib/fr_FR.json';
// Core viewer
import { Viewer, Worker, SpecialZoomLevel, LocalizationMap, LocalizationContext } from '@react-pdf-viewer/core';
// Plugins
import { toolbarPlugin, ToolbarSlot } from '@react-pdf-viewer/toolbar';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
// Import the styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
// Note: Zoom plugin typically doesn't have its own CSS, it uses core styles

interface ClientSidePdfViewerProps {
  pdfUrl: string;
  initialPageNumber?: number;
  onPageChange?: (pageNumber: number) => void;
  onDocumentLoadSuccess?: (numPages: number) => void;
  title: string;
}

const ClientSidePdfViewer: React.FC<ClientSidePdfViewerProps> = ({
  pdfUrl,
  initialPageNumber = 1,
  onPageChange,
  onDocumentLoadSuccess,
  title,
}) => {
  const theme = useTheme();
  const [currentFile, setCurrentFile] = useState(pdfUrl);
  const [key, setKey] = useState(pdfUrl); // To force re-render viewer on pdfUrl change

  // State for localization context
  // We initialize it with our French map.
  // The setL10n function could be used if you wanted to dynamically change languages later.
  const [l10n, setL10n] = useState(fr_FR as unknown as LocalizationMap);
  const localizationContextValue = { l10n, setL10n };

  useEffect(() => {
    // When pdfUrl changes, update the state and the key to re-initialize the viewer
    if (pdfUrl !== currentFile) {
        setCurrentFile(pdfUrl);
        setKey(pdfUrl); // Changing the key will unmount and remount the Viewer
    }
  }, [pdfUrl, currentFile]);

  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const zoomPluginInstance = zoomPlugin();

  const handlePageChange = (e: { currentPage: number }) => {
    if (onPageChange) {
      onPageChange(e.currentPage + 1); // react-pdf-viewer is 0-indexed for currentPage
    }
  };

  const handleDocumentLoad = (e: { doc: { numPages: number } }) => {
    if (onDocumentLoadSuccess) {
      onDocumentLoadSuccess(e.doc.numPages);
    }
  };

  if (!pdfUrl) {
    return (
      <Box sx={{ padding: 2, textAlign: 'center', color: theme.palette.text.secondary }}>
        <Typography variant="body1">No PDF URL provided.</Typography>
      </Box>
    );
  }
  
  return (
    <LocalizationContext.Provider value={localizationContextValue}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          padding: '5px',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '0px 20px 20px 0px',
        }}
        key={key} // Ensure viewer re-renders when the PDF source changes
      >
              <Box sx={{ display: 'flex', alignText: 'center', alignItems: 'center', padding: '20px' }}>
                  <Typography sx={{
                    width: '100%', 
                    justifyContent: 'center', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    color: 'black', 
                    fontSize: "20px", 
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: '700', wordWrap: 'break-word'}} >
                      {title}
                  </Typography>
              </Box>
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: '#fff',
            display: 'flex',
            padding: theme.spacing(1),
            justifyContent: 'center', // Center the toolbar content
          }}
        >
          <Toolbar>
            {(props: ToolbarSlot) => {
              const {
                CurrentPageLabel,
                Download,
                GoToNextPage,
                GoToPreviousPage,
                NumberOfPages,
                ZoomIn,
                ZoomOut,
              } = props;
              return (
                <>
                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '10px' }}>
                  <Box sx={{ padding: '0px' }}><ZoomIn /></Box>
                  <Box sx={{ padding: '0px' }}><ZoomOut /></Box>
                </Box>
                  <Box sx={{ padding: '0px', marginLeft: 'auto' }}></Box>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #e0e0e0',
                    padding: '5px 15px 5px 15px',
                    borderRadius: '10px',
                    fontSize: '12px',
                  }}>
                    <CurrentPageLabel /> /  <NumberOfPages />
                  </Box>
                  <Box sx={{ padding: '0 2px', display: 'none' }}><GoToNextPage /></Box>
                  <Box sx={{ padding: '0 2px', marginLeft: 'auto' }}><Download /></Box>
                  {/* Add other controls as needed */}
                </>
              );
            }}
          </Toolbar>
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', backgroundColor: theme.palette.grey[200], borderRadius: "inherit" }}>
          <Viewer
            fileUrl={currentFile}
            plugins={[
              toolbarPluginInstance,
              pageNavigationPluginInstance,
              zoomPluginInstance,
            ]}
            initialPage={initialPageNumber ? initialPageNumber -1 : 0} // 0-indexed
            onPageChange={handlePageChange}
            onDocumentLoad={handleDocumentLoad}
            defaultScale={SpecialZoomLevel.PageFit}
            localization={l10n}
          />
        </Box>
      </Box>
    </LocalizationContext.Provider>
  );
};

export default ClientSidePdfViewer;